import { create } from 'zustand'

interface ConversationSession {
  id: number
  userId: number
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced'
  topic?: string
  startTime: string
  status: 'in_progress' | 'completed' | 'interrupted'
}

interface ConversationExchange {
  id: number
  sessionId: number
  sequenceNumber: number
  speaker: 'user' | 'ai'
  textContent: string
  timestamp: string
}

interface ConversationState {
  currentSession: ConversationSession | null
  exchanges: ConversationExchange[]
  isRecording: boolean
  isWaitingForAI: boolean
  error: string | null
  isUsageLimitReached: boolean

  // Actions
  startSession: (session: ConversationSession) => void
  endSession: () => void
  addExchange: (exchange: ConversationExchange) => void
  setRecording: (recording: boolean) => void
  setWaitingForAI: (waiting: boolean) => void
  setError: (error: string | null) => void
  setUsageLimitReached: (value: boolean) => void
  reset: () => void
}

/**
 * Conversation store - manages active conversation state
 * Not persisted (session-only)
 */
export const useConversationStore = create<ConversationState>((set) => ({
  currentSession: null,
  exchanges: [],
  isRecording: false,
  isWaitingForAI: false,
  error: null,
  isUsageLimitReached: false,

  startSession: (session) =>
    set({
      currentSession: session,
      exchanges: [],
      error: null,
    }),

  endSession: () =>
    set((state) => ({
      currentSession: state.currentSession
        ? { ...state.currentSession, status: 'completed' }
        : null,
    })),

  addExchange: (exchange) =>
    set((state) => ({
      exchanges: [...state.exchanges, exchange],
    })),

  setRecording: (recording) => set({ isRecording: recording }),

  setWaitingForAI: (waiting) => set({ isWaitingForAI: waiting }),

  setError: (error) => set({ error }),

  setUsageLimitReached: (value) => set({ isUsageLimitReached: value }),

  reset: () =>
    set({
      currentSession: null,
      exchanges: [],
      isRecording: false,
      isWaitingForAI: false,
      error: null,
      isUsageLimitReached: false,
    }),
}))
